const API_BASE = "/api/v1";

class ApiClient {
  constructor() {
    this.token = null;
  }

  setToken(token) {
    this.token = token;
  }

  async request(path, options = {}) {
    const headers = {
      "Content-Type": "application/json",
      ...options.headers,
    };
    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      this.token = null;
      localStorage.removeItem("temto_token");
      // Only redirect if not already on login/register/public pages
      const pub = ["/login", "/register", "/welcome", "/forgot-password", "/reset-password"];
      if (!pub.some((p) => window.location.pathname.startsWith(p))) {
        window.location.href = "/login";
      }
      throw new Error("Unauthorized");
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }

    return response.json();
  }

  get(path) {
    return this.request(path);
  }

  post(path, data) {
    return this.request(path, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  put(path, data) {
    return this.request(path, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  patch(path, data) {
    return this.request(path, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  delete(path) {
    return this.request(path, { method: "DELETE" });
  }

  // Auth
  login(email, password) {
    return this.post("/auth/login", { email, password });
  }

  register(email, password) {
    return this.post("/auth/register", { email, password });
  }

  getMe() {
    return this.get("/auth/me");
  }

  googleAuth(idToken) {
    return this.post("/auth/google", { id_token: idToken });
  }

  forgotPassword(email) {
    return this.post("/auth/forgot-password", { email });
  }

  resetPassword(token, newPassword) {
    return this.post("/auth/reset-password", {
      token,
      new_password: newPassword,
    });
  }

  // Trades
  getTrades(skip = 0, limit = 50) {
    return this.get(`/trades/?skip=${skip}&limit=${limit}`);
  }

  createTrade(data) {
    return this.post("/trades/", data);
  }

  // Stripe
  createCheckout(priceId) {
    return this.post("/stripe/checkout", { price_id: priceId });
  }

  getSubscription() {
    return this.get("/stripe/subscription");
  }

  createPortalSession() {
    return this.post("/stripe/portal", {});
  }

  // Exchange
  getExchangeCredentials(exchange) {
    return this.get(`/exchange/${exchange}`);
  }

  saveExchangeCredentials(exchange, data) {
    return this.post(`/exchange/${exchange}`, data);
  }

  validateExchangeCredentials(exchange) {
    return this.post(`/exchange/${exchange}/validate`, {});
  }

  updateExchangeSettings(exchange, data) {
    return this.patch(`/exchange/${exchange}`, data);
  }

  deleteExchangeCredentials(exchange) {
    return this.delete(`/exchange/${exchange}`);
  }

  // Positions & Stats
  getOpenPositions() {
    return this.get("/positions/open");
  }

  getTradeHistory(skip = 0, limit = 50) {
    return this.get(`/positions/history?skip=${skip}&limit=${limit}`);
  }

  getPortfolioStats() {
    return this.get("/positions/stats");
  }

  getEquityCurve() {
    return this.get("/positions/equity-curve");
  }

  getRiskMetrics() {
    return this.get("/positions/risk");
  }

  // Trading Config
  getConfig() {
    return this.get("/config/trading");
  }

  updateConfig(data) {
    return this.patch("/config/trading", data);
  }

  resetConfig() {
    return this.post("/config/trading/reset", {});
  }

  // Copy Trading
  getLeaders() {
    return this.get("/copy-trading/leaders");
  }

  registerAsLeader(data) {
    return this.post("/copy-trading/leaders", data);
  }

  followLeader(leaderId, allocationPct = 100) {
    return this.post("/copy-trading/follow", {
      leader_id: leaderId,
      allocation_pct: allocationPct,
    });
  }

  unfollowLeader(leaderId) {
    return this.delete(`/copy-trading/follow/${leaderId}`);
  }

  getMyFollows() {
    return this.get("/copy-trading/followers");
  }

  updateFollowAllocation(followId, allocationPct) {
    return this.patch(`/copy-trading/followers/${followId}`, {
      allocation_pct: allocationPct,
    });
  }

  getMyFollowers() {
    return this.get("/copy-trading/my-followers");
  }

  // Sentiment
  getSentiment() {
    return this.get("/sentiment/current");
  }

  // Journal
  getJournalEntries(skip = 0, limit = 50) {
    return this.get(`/journal/?skip=${skip}&limit=${limit}`);
  }

  getJournalEntry(tradeId) {
    return this.get(`/journal/${tradeId}`);
  }

  updateJournalEntry(entryId, data) {
    return this.patch(`/journal/${entryId}`, data);
  }

  generateJournalAnalysis() {
    return this.post("/journal/generate", {});
  }

  // Backtest
  runBacktest(data) {
    return this.post("/backtest/run", data);
  }

  getBacktestResult(taskId) {
    return this.get(`/backtest/result/${taskId}`);
  }

  runGridBacktest(data) {
    return this.post("/backtest/grid/run", data);
  }

  // Admin
  getAdminStats() {
    return this.get("/admin/stats");
  }

  getAdminUsers(search = "") {
    return this.get(`/admin/users?search=${encodeURIComponent(search)}`);
  }

  // Read-only runtime/safety status (mirrors /readyz under /api/v1). GET only.
  getRuntimeStatus() {
    return this.get("/status");
  }

  // Read-only moonshot radar feed (M2d, admin-only, observe-only). GET only —
  // the feed is marked not_a_signal and never triggers any order or action.
  getRadarFeed() {
    return this.get("/radar/feed");
  }

  // Read-only moonshot M3 qualification report (admin-only, observe-only).
  // GET only — a readiness diagnosis (would_qualify/near_miss/…), never a
  // trade signal; the threshold is experimental, no validated edge.
  getRadarQualification() {
    return this.get("/radar/qualification");
  }

  // Operator cockpit (admin-only, all GET-only, read-only, no actions):
  // offline reconciliation report — no exchange account is ever checked.
  getReconReport() {
    return this.get("/recon/report");
  }

  // deterministic fixture parity report — never live data, never a correction.
  getParityReport() {
    return this.get("/parity/report");
  }

  // read-only guardian findings — observes only, never restarts/repairs.
  getGuardianReport() {
    return this.get("/guardian/report");
  }

  // evidence store status published observe-only by the worker.
  getEvidenceStatus() {
    return this.get("/evidence/status");
  }

  // Paper-entry control (admin-only, paper-only): read state + start/pause.
  // start/pause only toggle whether NEW paper entries are processed — no
  // order, no exchange, no live gate, no container action. EXIT/protective
  // logic in the worker is never gated by this.
  getPaperControl() {
    return this.get("/paper/control");
  }

  startPaperEntries() {
    return this.post("/paper/control/start", {});
  }

  pausePaperEntries() {
    return this.post("/paper/control/pause", {});
  }

  getHealthDetailed() {
    return this.get("/health/detailed");
  }

  // Grid Trading
  enableGrid(symbol, params) {
    const qs = new URLSearchParams({
      reference_price: params.reference_price,
      spacing_pct: params.spacing_pct || 2.0,
      num_grids: params.num_grids || 10,
      max_exposure_pct: params.max_exposure_pct || 50.0,
    });
    return this.post(`/grid-trading/enable/${symbol}?${qs}`, {});
  }

  disableGrid(symbol) {
    return this.post(`/grid-trading/disable/${symbol}`, {});
  }

  windDownGrid(symbol) {
    return this.post(`/grid-trading/wind-down/${symbol}`, {});
  }

  getGridStatus(symbol) {
    return this.get(`/grid-trading/status/${symbol}`);
  }

  listActiveGrids() {
    return this.get("/grid-trading/list");
  }

  // API Keys
  listApiKeys() {
    return this.get("/api-keys/");
  }

  createApiKey(data) {
    return this.post("/api-keys/", data);
  }

  revokeApiKey(keyId) {
    return this.delete(`/api-keys/${keyId}`);
  }

  // Webhooks
  listWebhooks() {
    return this.get("/webhooks/");
  }

  createWebhook(data) {
    return this.post("/webhooks/", data);
  }

  toggleWebhook(id) {
    return this.post(`/webhooks/${id}/toggle`, {});
  }

  testWebhook(id) {
    return this.post(`/webhooks/${id}/test`, {});
  }

  deleteWebhook(id) {
    return this.delete(`/webhooks/${id}`);
  }

  // Marketplace
  listStrategies(sort = "rating") {
    return this.get(`/marketplace/?sort=${sort}`);
  }

  publishStrategy(data) {
    return this.post("/marketplace/", data);
  }

  subscribeStrategy(id) {
    return this.post(`/marketplace/${id}/subscribe`, {});
  }

  unsubscribeStrategy(id) {
    return this.delete(`/marketplace/${id}/subscribe`);
  }

  rateStrategy(id, rating) {
    return this.post(`/marketplace/${id}/rate`, { rating });
  }

  myStrategies() {
    return this.get("/marketplace/my-strategies");
  }

  // AI Insights
  getAnomalyAlerts(unreadOnly = false) {
    return this.get(`/anomaly/alerts?unread_only=${unreadOnly}`);
  }

  markAnomalyAlertsRead() {
    return this.post("/anomaly/alerts/read", {});
  }

  runAnomalyScan() {
    return this.post("/anomaly/scan", {});
  }

  getTradePatterns() {
    return this.get("/patterns/analyze");
  }

  getWeeklySummary() {
    return this.get("/patterns/weekly-summary");
  }
}

export const api = new ApiClient();
export default api;
